import React from 'react';
import firebase from '../../firebase';
import { v4 as uuidv4 } from 'uuid';
import { Segment, Button, Input, Popup } from 'semantic-ui-react';
import { Picker } from 'emoji-mart';
import 'emoji-mart/css/emoji-mart.css';

import FileUploadModal from './FileUploadModal';
import VoiceModal from './VoiceModal';
import ProgressBar from './ProgressBar';

class MessagesInputArea extends React.Component {

  // Initial State
  state = {
    storageRef: firebase.storage().ref(), 
    uploadTask: null, 
    uploadState: '', 
    percentUploaded: 0, 
    message: '',
    channel: this.props.currentChannel,
    user: this.props.currentUser,
    loading: false,
    errors: [], 
    modal: false,
    voiceModal: false, 
    emojiPicker: false
  };
  
  // React Life Cycle Methods
  componentWillUnmount() {
    if (this.state.uploadTask !== null) {
      this.state.uploadTask.cancel();
      this.setState({ uploadTask: null });
    }
  }

  openModal = () => this.setState({ modal: true });
  closeModal = () => this.setState({ modal: false });

  openVoiceModal = () => this.setState({ voiceModal: true });
  closeVoiceModal = () => this.setState({ voiceModal: false });

  // Handles Messages Change
  handleChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  // Toggles Emoji Picker
  handleTogglePicker = () => {
    this.setState({ emojiPicker: !this.state.emojiPicker });
  }

  // Close Emoji Picker
  closeTogglePicker = () => {
    this.setState({ emojiPicker: this.state.emojiPicker });
  }

  // Handles adding of Emojis
  handleAddEmoji = emoji => {
    const oldMessage = this.state.message;
    const newMessage = oldMessage + emoji.native;
    this.setState({ message: newMessage });
    setTimeout(() => this.messageInputRef.focus(), 0);
  };

  // Creates message if it has no fileUrl then it's a normal message otherwise an embedded message
  createMessage = (fileUrl = null) => {
    const message = {
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      user: {
        id: this.state.user.uid,
        name: this.state.user.displayName,
        avatar: this.state.user.photoURL
      },
      
    };
    if (fileUrl !== null) {
      message['image'] = fileUrl;
    } else {
      message['content'] = this.state.message;
    }
    return message;
  };

  // When enter is pressed while typing message this is triggered
  handleKeyDown = event => {
    if(event.keyCode === 13) {
      this.sendMessage();
    }
  }

  // Sends the message and appends values to firebase
  sendMessage = () => {
    const { getMessagesRef } = this.props;
    const { message, channel } = this.state;

    if (message) {
      this.setState({ loading: true });
      getMessagesRef()
        .child(channel.id)
        .push()
        .set(this.createMessage())
        .then(() => {
          this.setState({ loading: false, message: "", errors: [] });
        })
        .catch(err => {
          console.error(err);
          this.setState({
            loading: false,
            errors: this.state.errors.concat(err)
          });
        });
    } else {
      this.setState({
        errors: this.state.errors.concat({ message: "Add a message" })
      });
    }
  };

  // Gets path of stored file in the firebase
  getPath = () => {
    if (this.props.isPrivateChannel) {
      return `chat/private-${this.state.channel.id}`;
    } else {
      return 'chat/public'
    }
  }

  // Handles the upload of a file
  uploadFile = (file, metadata) => {
    const pathToUpload = this.state.channel.id;
    const ref = this.props.getMessagesRef();
    const filePath = `${this.getPath()}/${uuidv4()}.jpg`

    this.setState({
      uploadState: 'uploading', 
      uploadTask: this.state.storageRef.child(filePath).put(file, metadata)
    }, 
      () => {
        this.state.uploadTask.on('state_changed', snap => {
          const percentUploaded = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          this.setState({percentUploaded});
        }, 
          err => {
            console.error(err);
            this.setState({
              errors: this.state.errors.concat(err), 
              uploadState: 'error', 
              uploadTask: null
            })
          }, 

            () => {
              this.state.uploadTask.snapshot.ref.getDownloadURL().then(downloadUrl => {
                this.sendFileMessage(downloadUrl, ref, pathToUpload);
              })
              .catch(err => {
                console.error(err);
                this.setState({
                  errors: this.state.errors.concat(err), 
                  uploadState: 'error', 
                  uploadTask: null
                })
              })
            }
          )
      }
    )
  };

  // Sends a file embedded message
  sendFileMessage = (fileUrl, ref, pathToUpload) => {
    ref.child(pathToUpload)
       .push()
       .set(this.createMessage(fileUrl))
       .then(() => {
         this.setState({ uploadState: 'done' })
       })
       .catch(err => {
         console.error(err);
         this.setState({
           errors: this.state.errors.concat(err)
         })
       })
  }

  // Uploads voice message to the firebase
  uploadVoice = (voiceFile, voiceMetadata) => {
    const voicePathToUpload = this.state.channel.id;
    const voiceRef = this.props.getMessagesRef();
    const voiceFilePath = `${this.getPath()}/${uuidv4()}.mp3`

    this.setState({
      uploadState: 'uploading', 
      uploadTask: this.state.storageRef.child(voiceFilePath).put(voiceFile, voiceMetadata)
    }, 
      () => {
        this.state.uploadTask.on('state_changed', snap => {
          const percentUploaded = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          this.setState({percentUploaded});
        }, 
          err => {
            console.error(err);
            this.setState({
              errors: this.state.errors.concat(err), 
              uploadState: 'error', 
              uploadTask: null
            })
          }, 

            () => {
              this.state.uploadTask.snapshot.ref.getDownloadURL().then(downloadUrl => {
                this.sendFileMessage(downloadUrl, voiceRef, voicePathToUpload);
              })
              .catch(err => {
                console.error(err);
                this.setState({
                  errors: this.state.errors.concat(err), 
                  uploadState: 'error', 
                  uploadTask: null
                })
              })
            }
          )
      }
    )
  };

  // Uses Semantic UI React Components and above created functions and renders it according to state changes
  render() {
    const { errors, message, loading, modal, uploadState, percentUploaded, emojiPicker, voiceModal } = this.state;

    return (
      <Segment className="message_form">
        <Input
          fluid
          name="message"
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          value={message}
          ref={node => (this.messageInputRef = node)}
          style={{ marginBottom: "0.7em" }}
          label={<Popup
                  menu={{ pointing: true }}
                  trigger={<Button icon="smile outline"
                            onClick={ emojiPicker ? this.closeTogglePicker : this.handleTogglePicker}/>}
                  content={emojiPicker && (
                  <Picker set="google" onSelect={this.handleAddEmoji} className="emojipicker" 
                  title="Pick your emoji" emoji="point_up"/>)}
                  on='click'
                  />}
          labelPosition="left"
          className={
            errors.some(error => error.message.includes("message")) ? "error" : ""
          }
          placeholder="Write your message"
        />
        <Button.Group icon widths="3">
          <Button
            onClick={this.sendMessage}
            disabled={loading}
            color="black"
            content="Send Message"
            labelPosition="left"
            icon="edit"
          />
          <Button
            onClick={this.openModal}
            color="black"
            disabled={uploadState === "uploading"}
            content="Upload Image"
            labelPosition="middle"
            icon="cloud upload"
          />
          <Button
            onClick={this.openVoiceModal}
            color="black"
            disabled={uploadState === "uploading"}
            content="Voice Message"
            labelPosition="left"
            icon="microphone"
          />
          <VoiceModal 
            voiceModal={voiceModal}
            closeVoiceModal={this.closeVoiceModal}
            uploadVoice={this.uploadVoice}
          />          
        </Button.Group>
        <FileUploadModal 
          modal={modal}
          closeModal={this.closeModal}
          uploadFile={this.uploadFile}
        />
        <ProgressBar 
          uploadState={uploadState}
          percentUploaded={percentUploaded}
        />
      </Segment>
    );
  }
}

export default MessagesInputArea;